'use client';

import { IMAGE_MANIFEST } from '~/data/image-manifest';
import type React from 'react';

interface Props {
  src: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * ResponsiveImage component handles three URL types:
 * 1. Unsplash URLs - adds WebP format + srcset for multiple sizes
 * 2. Local /images/* - looks up WebP variant in IMAGE_MANIFEST
 * 3. Everything else - plain <img> with correct defaults
 *
 * Always renders width/height to prevent CLS.
 * priority=true sets fetchpriority="high" + loading="eager"
 */
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  priority = false,
  className,
  style,
}: Props) {
  const isUnsplash = src.includes('images.unsplash.com');
  const isLocal = src.startsWith('/images/');

  // Common image props
  const imgProps = {
    alt,
    width,
    height,
    className,
    style,
    loading: priority ? ('eager' as const) : ('lazy' as const),
    decoding: 'async' as const,
    ...(priority && { fetchPriority: 'high' as const }),
  };

  // Handle Unsplash URLs with WebP format and srcset
  if (isUnsplash) {
    const baseUrl = src.split('?')[0]!; // Remove existing params
    const separator = src.includes('?') ? '&' : '?';
    const webpUrl = `${baseUrl}${separator}fm=webp`;

    // Build srcset for multiple device widths
    const srcSet = `${webpUrl}&w=400 400w, ${webpUrl}&w=800 800w`;

    return (
      <picture>
        <source srcSet={srcSet} type="image/webp" />
        <img src={src} {...imgProps} />
      </picture>
    );
  }

  // Handle local images with WebP variants
  if (isLocal) {
    const webpSrc = (IMAGE_MANIFEST as Record<string, string | undefined>)[src];

    if (webpSrc) {
      return (
        <picture>
          <source srcSet={webpSrc} type="image/webp" />
          <img src={src} {...imgProps} />
        </picture>
      );
    }
  }

  // Default: plain <img> for everything else (DB headshots, Yelp CDN, etc.)
  return <img src={src} {...imgProps} />;
}
